import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CallGetProducts, CallPostProducts, CallPutProducts } from '@library/request/callProduct';
import { numberWithCommas } from '@library/Utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CommonRadioButton from '@components/molecules/radioButton/RadioButton';
import ImageInput from '@components/molecules/input/ImageInput';
import Spinner from '@components/molecules/Spinner';
import { InitProduct, MENUTYPE, PRODUCT_KEYS, Products } from '@type/productType';
import { useMenuPermission } from '@src/recoil/permission';
import CommonCheckBox from '@components/molecules/checkBox/CheckBox';

const initProduct: InitProduct = {
  name: '',
  productDescription: '',
  status: '1',
  isDisplay: '1',
  recommend: '0',
  alcohol: '0',
  imageUrl: '',
  options: [{ isPriceOption: '1', title: '가격', type: 1, children: [{ name: '', price: '', alcohol: '0' }] }],
};

// 메뉴 추가/정보수정
const MenuAdd = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const storeId = searchParams.get('storeID');
  const categoryID = searchParams.get('categoryID');
  const productID = searchParams.get('productID');

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState<Products | InitProduct>();
  const [step, setStep] = useState(1);
  const [isDirty, setIsDirty] = useState(false); // 수정 여부

  const { menuPermission } = useMenuPermission(storeId);
  const isEdit = !!productID;

  const btnDisable = useMemo(() => {
    if (!menuPermission.isFetch || !menuPermission.data?.[0]) {
      return { priceAdd: false, priceInput: false, allItem: false };
    }

    const simplePermission = menuPermission.data[0];
    const isModifyPermission = simplePermission.permissionType === 2;
    const priceInput = isModifyPermission && simplePermission.price === 0;
    const allItem = isModifyPermission && simplePermission.information === 0;

    return { priceAdd: isModifyPermission, priceInput, allItem };
  }, [menuPermission]);

  // 메뉴 조회 API
  const { data: productDetail } = useQuery<Products>([PRODUCT_KEYS.PRODUCT, storeId, productID], {
    enabled: !!storeId && !!productID,
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetProducts(storeId, productID);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 메뉴 추가 API
  const { mutate: productAddMutation } = useMutation((param: any) => CallPostProducts(storeId, param), {
    onMutate: () => setLoading(true),
    onSuccess: (response) => {
      const { result } = response;
      if (result.code !== 1) {
        alert(result.message);
      } else {
        alert('메뉴가 추가되었습니다.');
        queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]);
        queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCT, storeId]); // 메뉴 캐싱 삭제
        navigate(`/product/list?tab=menu&storeID=${storeId}`);
      }
    },
    onError: (error) => console.log('error:', error),
    onSettled: () => setLoading(false),
  });

  // 메뉴 수정 API
  const { mutate: productEditMutation } = useMutation((param: any) => CallPutProducts(storeId, productID, param), {
    onMutate: () => setLoading(true),
    onSuccess: (response) => {
      const { result } = response;
      if (result.code !== 1) {
        alert(result.message);
      } else {
        alert('메뉴 정보 수정이 완료되었습니다.');
        queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]);
        queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCT, storeId, productID]); // 메뉴 캐싱 삭제
        navigate(`/product/list?tab=menu&storeID=${storeId}`);
      }
    },
    onError: (error) => console.log('error:', error),
    onSettled: () => setLoading(false),
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let { value, name, id } = e.target;

    if (name === 'name') {
      value = value.slice(0, 255);
    }

    setProduct((prev) => {
      const updates = { [name]: value };

      // options 업데이트 함수
      const updateOptionsAlcohol = (alcoholValue: string) => {
        const updatedOptions = [...prev.options];
        updatedOptions[0] = {
          ...updatedOptions[0],
          children: updatedOptions[0].children.map((child) => ({
            ...child,
            alcohol: alcoholValue,
          })),
        };
        return updatedOptions;
      };

      // 일반메뉴 or 주류메뉴
      if (id === 'origin' || id === 'alcohol') {
        return {
          ...prev,
          ...updates,
          options: updateOptionsAlcohol(value),
        };
      }

      // 주류라벨
      if (id === 'label') {
        updates.alcohol = MENUTYPE.alcohol;
        return {
          ...prev,
          ...updates,
          options: updateOptionsAlcohol(MENUTYPE.origin),
        };
      }

      return { ...prev, ...updates };
    });
    setIsDirty(true);
  };

  // 메뉴 이미지 변경
  const onChangeImg = (imageUrl: string) => {
    setProduct((prev: any) => ({ ...prev, imageUrl }));
    setIsDirty(true);
  };

  // 가격 설정 삭제
  const onClickPriceDelete = (index: number) => {
    setProduct((prev) => {
      const updateOptions = [...prev.options];
      updateOptions[0] = {
        ...updateOptions[0],
        children: updateOptions[0].children.filter((_: any, i: number) => i !== index),
      };
      return { ...prev, options: updateOptions };
    });
    setIsDirty(true);
  };

  // 가격 설정 수정
  const onChangePrice = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { id, value } = e.target;
    const inputVal = id === 'price' ? Number(value.toString().replace(/[^\d]/g, '')) : value;

    setProduct((prev) => {
      const updatedOptions = [...prev.options];
      updatedOptions[0] = {
        ...updatedOptions[0],
        children: updatedOptions[0].children.map((data, i: number) =>
          i === index ? { ...data, [id]: inputVal } : data,
        ),
      };

      return { ...prev, options: updatedOptions };
    });
    setIsDirty(true);
  };

  // 가격 설정 추가
  const onClickPriceAdd = () => {
    setProduct((prev) => {
      // 주류메뉴인경우
      const isAlcoholMenu = product?.alcohol === MENUTYPE.alcohol && isAllPriceOptAlcohol;

      const updatedOptions = [...prev.options];
      updatedOptions[0] = {
        ...updatedOptions[0],
        children: updatedOptions[0].children.concat({ name: '', price: 0, alcohol: isAlcoholMenu ? '1' : '0' }),
      };

      return { ...prev, options: updatedOptions };
    });
    setIsDirty(true);
  };

  // 가격 설정 > 주류 체크박스 변경
  const onChangeAlcohol = (index: number) => {
    setProduct((prev: any) => {
      const updatedOptions = [...prev.options];
      updatedOptions[0] = {
        ...updatedOptions[0],
        children: updatedOptions[0].children.map((data: any, i: number) =>
          i === index
            ? { ...data, alcohol: data.alcohol === MENUTYPE.alcohol ? MENUTYPE.origin : MENUTYPE.alcohol }
            : data,
        ),
      };

      return { ...prev, options: updatedOptions };
    });
    setIsDirty(true);
  };

  // 저장
  const onClickSave = () => {
    const param = {
      ...product,
      options: product.options.map((option) => {
        // 주류메뉴이고 isPriceOption = 1(가격옵션)이면 children.alcohol = 1 변경해서 보내기 =>(변경된 주류 설정 기능으로 인하여 삭제)
        if (option.isPriceOption === '1') {
          return {
            ...option,
            children: option.children.map((child) => ({
              ...child,
              originPrice: child.price, // 메뉴 등록/수정 시 originPrice 키 제거
            })),
          };
        }
        return option;
      }),
      ...(categoryID ? { categoryID } : {}),
      // 가격설정 추가/삭제 시 해당 메뉴 노출여부 갱신
      isDisplay: product.options.some((opt) =>
        opt.children.some((child) => Number(child?.isDisplay) > 0 || !child.isDisplay),
      )
        ? '1'
        : '0',
    };

    isEdit ? productEditMutation(param) : productAddMutation(param);
  };

  // 저장 버튼 비활성화 조건
  const isSaveBtnDisable = useMemo(() => {
    return (
      !product?.name ||
      !product?.options?.some(
        (optionGroup) =>
          Number(optionGroup.isPriceOption) === 1 && optionGroup.children?.every((val) => Number(val.price) > 99),
      )
    );
  }, [product]);

  // 가격 옵션
  const priceOption = useMemo(() => {
    return product?.options?.find((option) => Number(option.isPriceOption) === 1);
  }, [product?.options]);

  // 모든 가격옵션의 alcohol이 1인지
  const isAllPriceOptAlcohol: boolean = useMemo(() => {
    if (!priceOption) return;
    return priceOption?.children.every((child: any) => child.alcohol === MENUTYPE.alcohol);
  }, [product?.options]);

  useEffect(() => {
    if (isEdit && productDetail) {
      setProduct(productDetail); // 수정 (detail)
    } else {
      setProduct(initProduct); // 추가 (new)
    }
  }, [productDetail]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <article id="mArticle" className="art-product menu-add">
      {!isEdit && (
        <span className="txt-step">
          <span className="num">{step}</span> / 2
        </span>
      )}
      <div className="view-product">
        {step === 1 ? (
          <>
            <div className="box-product">
              <strong className="lab-product">
                메뉴 설정<span className="mark-ess">*</span>
              </strong>
              <CommonRadioButton
                disabled={btnDisable.allItem}
                name="alcohol"
                id="origin"
                value={MENUTYPE.origin}
                label="일반 메뉴입니다."
                check={product?.alcohol === MENUTYPE.origin}
                onChangeHandler={(e) => onChange(e)}
              />
              <CommonRadioButton
                disabled={btnDisable.allItem}
                name="alcohol"
                id="label"
                value={MENUTYPE.alcohol}
                label={
                  <p>
                    <span className="alcohol-badge">19</span>주류 라벨만 표기되는 메뉴입니다.
                  </p>
                }
                check={product?.alcohol === MENUTYPE.alcohol && !isAllPriceOptAlcohol}
                onChangeHandler={(e) => onChange(e)}
              />
              <CommonRadioButton
                disabled={btnDisable.allItem}
                name="alcohol"
                id="alcohol"
                value={MENUTYPE.alcohol}
                label={
                  <p>
                    <span className="alcohol-badge">19</span>주류 메뉴입니다.
                  </p>
                }
                check={product?.alcohol === MENUTYPE.alcohol && isAllPriceOptAlcohol}
                onChangeHandler={(e) => onChange(e)}
              />
            </div>
            <div className="box-product">
              <strong className="lab-product">
                메뉴명<span className="mark-ess">*</span>
              </strong>
              <input
                disabled={btnDisable.allItem}
                type="text"
                name="name"
                id="name"
                className="tf-cont"
                placeholder="예)불고기피자"
                data-string="text"
                value={product?.name || ''}
                onChange={onChange}
                maxLength={255}
              />
            </div>
            <div className="box-product">
              <strong className="lab-product">한줄 설명</strong>
              <textarea
                disabled={btnDisable.allItem}
                id="productDescription"
                name="productDescription"
                className="tf-cont"
                placeholder="예)가장 인기 있는 베스트 메뉴 모음입니다."
                data-string="text"
                value={product?.productDescription || ''}
                onChange={onChange}
              />
            </div>
            <div className="box-product">
              <strong className="lab-product">메뉴 이미지</strong>
              <ImageInput
                url={product?.imageUrl || ''}
                onChangeHandler={onChangeImg}
                onClickDelete={() => !btnDisable.allItem && setProduct((prev: any) => ({ ...prev, imageUrl: '' }))}
              />
            </div>
            <div className="box-product">
              <strong className="lab-product">
                가격 설정<span className="mark-ess">*</span>
              </strong>
              {priceOption?.children.some((data) => Number(data.menuCouponCount) > 0) && (
                <p className="desc-product">메뉴 쿠폰이 발급된 메뉴 및 옵션은 파트너스에서 가격 수정이 불가합니다.</p>
              )}
              <ul className="list-price">
                {priceOption?.children?.map((list, index: number) => {
                  const { name, price, menuCouponCount = 0, alcohol } = list;
                  const hasMenuCoupon = isEdit && Number(menuCouponCount) > 0;
                  return (
                    <li key={index}>
                      <input
                        disabled={hasMenuCoupon || btnDisable.allItem}
                        type="text"
                        name="name"
                        id="name"
                        className="tf-cont"
                        placeholder="예)1인분"
                        value={name || ''}
                        onChange={(e) => onChangePrice(e, index)}
                      />
                      <div className="inputLabel">
                        <input
                          disabled={hasMenuCoupon || btnDisable.priceInput}
                          type="text"
                          id="price"
                          className="tf-cont"
                          placeholder="100원 이상 입력"
                          autoComplete="off"
                          value={price ? numberWithCommas(price) : ''}
                          onChange={(e) => onChangePrice(e, index)}
                        />
                        <span>원</span>
                        {priceOption?.children.length > 1 && (
                          <button disabled={btnDisable.allItem} onClick={() => onClickPriceDelete(index)}>
                            삭제
                          </button>
                        )}
                      </div>
                      <div className="alcohol-checkbox">
                        <p>
                          <span className="alcohol-badge">19</span>주류메뉴<span>(미성년자 판매불가)</span>
                        </p>
                        <CommonCheckBox
                          id={`isAlcohol-${index}`}
                          value={alcohol === MENUTYPE.alcohol}
                          miniSize
                          onChange={() => onChangeAlcohol(index)}
                          disabled={
                            // 일반메뉴 or 주류메뉴인경우 비활성화
                            product?.alcohol === MENUTYPE.origin ||
                            (product?.alcohol === MENUTYPE.alcohol && isAllPriceOptAlcohol)
                          }
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>

              <button className="btn-round-add" onClick={onClickPriceAdd} disabled={btnDisable.priceAdd}>
                <span className="ico-plus" />
                추가
              </button>
            </div>
          </>
        ) : (
          <div className="box-product">
            <strong className="lab-product">메뉴 확인</strong>
            <p className="desc-product">
              등록한 메뉴 정보가 맞는지 확인 후 저장 버튼을 클릭해 주시면 내 매장에 메뉴가 등록됩니다.
            </p>
            <div className="list-product-option">
              <dl className="list-option">
                <dt>메뉴명</dt>
                <dd>{product?.name || ''}</dd>
              </dl>
              <dl className="list-option">
                <dt>한줄 설명</dt>
                <dd>{product?.productDescription || ''}</dd>
              </dl>
              <dl className="list-option">
                <dt>가격 설정</dt>
                <dd>
                  {product && priceOption
                    ? priceOption.children?.map((option, index) => (
                        <React.Fragment key={index}>
                          {option.name || '기본'}{' '}
                          {option?.alcohol === MENUTYPE.alcohol && <span className="alcohol-badge">19</span>}
                          {`: ${numberWithCommas(option.price)}원`}
                          <br />
                        </React.Fragment>
                      ))
                    : ''}
                </dd>
              </dl>
            </div>
          </div>
        )}

        <div className="fixedBtnGroup">
          {isEdit ? (
            <button
              type="button"
              id="btnSubmit"
              className="btnDone"
              disabled={isSaveBtnDisable || !isDirty}
              onClick={onClickSave}
            >
              저장
            </button>
          ) : (
            <>
              <button
                type="button"
                id="btnSubmit"
                className={step === 1 ? 'btnDone' : 'btnEdit'}
                disabled={step === 1 ? isSaveBtnDisable : false}
                onClick={() => (step === 1 ? setStep(2) : setStep(1))}
              >
                {step === 1 ? '다음' : '이전'}
              </button>
              {step === 2 && (
                <button type="button" id="btnSubmit" className="btnDone" onClick={onClickSave}>
                  저장
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <Spinner show={loading} />
    </article>
  );
};

export default MenuAdd;
